import { ChangeDetectorRef, Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ImportType } from "./import.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Bind } from "../../decorators/bind.decorator";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types.expanded";
import { CustomAttribute, EventI, EventProfile } from "../../pojo/EventI";
import { jSith } from "../../util/jquery-replacement";
import { ContactService } from "../../services/contact.service";
import { SpaceService } from "../../services/space.service";
import { Conflicts, EventImportSaveResponseDto, ImportService } from "./import.service";
import { NameService } from "../../services/name.service";
import { Item } from "../../pojo/Item";
import { EventService } from "../../services/event.service";
import { S25Util } from "../../util/s25-util";
import { UserprefService } from "../../services/userpref.service";
import { EventTypeService } from "../../services/master.definitions/event.misc/event.type.service";
import { OrganizationService } from "../../services/organization.service";
import { EventCategoryService } from "../../services/master.definitions/categories/event.category.service";
import { CustomAttributeEventService } from "../../services/master.definitions/custom.attributes/custom.attribute.event.service";
import { EventSpreadsheetUtil } from "./util/event/event.spreadsheet.util";
import { ImportUtil } from "./util/import.util";
import { EventImportUtil } from "./util/event/event.import.util";
import { ResourceService } from "../../services/resource.service";
import { ScrollLockService } from "../../services/scroll.lock.service";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";

@Component({
    selector: "s25-ng-import-events",
    template: `
        @if (init) {
            <div class="c-margin-bottom--single">
                Select a fallback folder for unrouted events. Or select an override folder to force all events to be
                imported into that folder.
            </div>

            @if (!overrideFolder) {
                <div class="c-margin-bottom--single">
                    <span>Fallback Folder&nbsp;</span>
                    <s25-ng-dropdown-search-criteria
                        class="ngInlineBlock"
                        [type]="'eventFolders'"
                        [(value)]="fallbackFolder"
                        [placeholder]="'Select a fallback folder'"
                        [blockDetailsNav]="true"
                        [renderItem]="false"
                    />
                    <button class="c-margin-left--half aw-button aw-button--outline" (click)="fallbackFolder = null">
                        Clear
                    </button>
                </div>
            }

            @if (!fallbackFolder) {
                <div class="c-margin-bottom--single">
                    <span>Override Folder&nbsp;</span>
                    <s25-ng-dropdown-search-criteria
                        class="ngInlineBlock"
                        [type]="'eventFolders'"
                        [(value)]="overrideFolder"
                        [placeholder]="'Select an override folder'"
                        [blockDetailsNav]="true"
                        [renderItem]="false"
                    />
                    <button class="c-margin-left--half aw-button aw-button--outline" (click)="overrideFolder = null">
                        Clear
                    </button>
                </div>
            }

            <div class="c-margin-bottom--single">
                <label>
                    <input type="checkbox" [(ngModel)]="moveConflictsToPreferences" />
                    Move conflicts to preferences
                </label>
            </div>

            <div class="c-margin-bottom--single c-margin-top--single">
                <button class="aw-button aw-button--outline" (click)="downloadTemplate()">
                    Download Excel Template
                </button>
            </div>
        }
    `,
    imports: [FormsModule, S25DropdownPaginatedModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
})
export class S25ImportEventsComponent implements OnInit {
    @Input() type: ImportType;
    @Input() parentCd?: ChangeDetectorRef;

    constructor(private cd: ChangeDetectorRef) {}

    init = false;
    fallbackFolder: DropDownItem | null = null;
    overrideFolder: DropDownItem | null = null;
    moveConflictsToPreferences = false;
    timeFormat: string;
    dateTimeFormat: string;
    eventTypeNameMap = new Map<string, number>();
    orgNameMap = new Map<string, number>();
    catNameMap = new Map<string, number>();
    custAttrNameMap = new Map<string, CustomAttribute>();

    @Bind
    async onCompleteEvents(rows: RowData[]): Promise<void> {
        try {
            this.type.errors = [];
            this.type.conflicts = undefined;
            this.type.successMessage = "";

            if (!rows || !rows.length) {
                this.type.errors.push("No sheet data found");
                this.refresh();
                return;
            }
            const extUidToEvent = new Map<string, EventI>();
            const extUidToProfile = new Map<string, EventProfile>();
            const rowsProcessedDefer = jSith.defer();
            const contNameMapDefer = jSith.defer();
            const locNameMapDefer = jSith.defer();
            const resNameMapDefer = jSith.defer();
            const contEmailSet = new Set<string>();
            const locNameSet = new Set<string>();
            const resNameSet = new Set<string>();
            const errorSet = new Set<string>();
            const postProcessPromises: Promise<unknown>[] = [];

            // Extract headers from first row
            const cellNumToHeader: Map<number, string> = new Map<number, string>();
            if (rows && rows.length > 0 && rows[0]?.cells) {
                for (let c = 0; c < rows[0].cells.length; c++) {
                    const cell = rows[0].cells[c];
                    if (cell?.text) {
                        cellNumToHeader.set(c, cell.text);
                    }
                }
            }

            let events = EventSpreadsheetUtil.rowsToEvents(
                extUidToEvent,
                extUidToProfile,
                contEmailSet,
                locNameSet,
                resNameSet,
                errorSet,
                rows,
                rowsProcessedDefer.promise,
                this.eventTypeNameMap,
                this.orgNameMap,
                this.catNameMap,
                this.custAttrNameMap,
                contNameMapDefer.promise,
                locNameMapDefer.promise,
                resNameMapDefer.promise,
                postProcessPromises,
            );

            events = events.filter((e) => !!e);
            if (!events || !events.length) {
                const requiredColumns = [
                    "EVENT_ID",
                    "EVENT_NAME",
                    "EVENT_TYPE",
                    "SCHEDULER",
                    "REQUESTOR",
                    "PROFILE_ID",
                    "FIRST_OCCURRENCE_DATE",
                    "START_TIME",
                    "END_TIME",
                    // Note: REPEATING_RULE is optional (can use ADHOC_DATES instead)
                ];
                const missingColumns = ImportUtil.getMissingRequiredColumns(cellNumToHeader, requiredColumns);

                // Check errorSet for missing EVENT_ID or PROFILE_ID values (columns exist but are empty)
                // Only include columns that have headers but missing values (not already in missingColumns)
                const missingValueColumns: string[] = [];
                if (errorSet.has("Missing EVENT_ID") && !missingColumns.includes("EVENT_ID")) {
                    missingValueColumns.push("EVENT_ID");
                }
                if (errorSet.has("Missing PROFILE_ID") && !missingColumns.includes("PROFILE_ID")) {
                    missingValueColumns.push("PROFILE_ID");
                }

                // Combine missing column headers and missing values, removing duplicates
                const allMissingColumns = [...new Set([...missingColumns, ...missingValueColumns])];

                if (allMissingColumns.length > 0) {
                    this.type.errors.push(
                        `No valid events found. Missing required columns: ${allMissingColumns.join(", ")}`,
                    );
                } else {
                    this.type.errors.push("No valid events found");
                }
                this.refresh();
                return;
            }

            // wait for all rows to be processed, which means contEmailSet and locNameSet are all filled and ready
            rowsProcessedDefer.resolve();

            // get locations and contacts by filtered names (emails) and form maps from name -> id
            // vvv filtered name -> id maps vvv
            const contNameMapPromise = ContactService.getContactsByEmails(Array.from(contEmailSet)).then((contacts) => {
                return contNameMapDefer.resolve(
                    contacts.reduce((map, cont) => {
                        if (cont.email && cont.contId !== undefined) {
                            cont.email = cont.email.trim();
                            map.set(cont.email, cont.contId);
                        }
                        return map;
                    }, new Map<string, number>()),
                );
            });

            const locNameMapPromise = SpaceService.getSpacesByName(Array.from(locNameSet)).then((locations) => {
                return locNameMapDefer.resolve(
                    locations.reduce((map, loc) => {
                        if (loc.roomShort && loc.roomId !== undefined) {
                            loc.roomShort = loc.roomShort.trim();
                            map.set(loc.roomShort, loc.roomId);
                        }
                        return map;
                    }, new Map<string, number>()),
                );
            });

            const resNameMapPromise = ResourceService.getResourcesByName(Array.from(resNameSet)).then((resources) => {
                return resNameMapDefer.resolve(
                    resources.reduce((map, res) => {
                        if (res.resourceName && res.resourceId !== undefined) {
                            res.resourceName = res.resourceName.trim();
                            map.set(res.resourceName, res.resourceId);
                        }
                        return map;
                    }, new Map<string, number>()),
                );
            });
            // ^^^ filtered name -> id maps ^^^

            // wait for them to finish
            await contNameMapPromise;
            await locNameMapPromise;
            await resNameMapPromise;

            // wait for each row to process the new filtered name -> id maps
            await Promise.all(postProcessPromises);

            if (errorSet.size) {
                // show any errors found during processing, like a missing name -> id mapping
                this.type.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            EventImportUtil.addOccurrences(events, errorSet);
            if (errorSet.size) {
                // show any errors found during processing, like a missing name -> id mapping
                this.type.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            EventImportUtil.bindProfiles(events);
            EventImportUtil.relateEvents(events);

            this.type.errors = EventImportUtil.validateEvents(events);
            if (this.type.errors.length) {
                this.refresh();
                return;
            }

            const response = await this.importEvents(events);
            if (response?.error) {
                if (response.error === "no_events") {
                    this.type.errors.push("No events to import");
                } else if (response.error === "inheritance_failed") {
                    this.type.errors.push("Folder inheritance failed");
                } else if (response.error === "conflict") {
                    this.type.errors.push("Conflict(s) detected");
                    if (response.reservationSaveResponse?.conflicts) {
                        this.processConflicts(response.reservationSaveResponse.conflicts);
                    }
                } else if (response.error === "fallback_folder_required") {
                    this.type.errors.push("Fallback folder is required for unrouted events");
                } else if (response.error === "no_reservations") {
                    this.type.errors.push("No reservations found for events");
                } else if (response.error === "sp_bulk_temp_table_creation_failed") {
                    this.type.errors.push("Failed to create temporary table for space reservations");
                } else if (response.error === "rs_bulk_temp_table_creation_failed") {
                    this.type.errors.push("Failed to create temporary table for resource reservations");
                } else {
                    this.type.errors.push("Unknown error: [" + response.error + "]");
                }
            } else if (!this.type.errors.length) {
                this.type.successMessage = "Events imported successfully";
            }

            this.refresh();
        } catch (ex) {
            const errorData = ex?.data || ex?.error;
            if (errorData?.error) {
                this.type.errors.push(errorData.error);
            } else {
                S25Util.showError(ex, "Failed to import events");
            }
            this.refresh();
        }
    }

    async processConflicts(conflicts: Conflicts) {
        if (!conflicts) {
            return;
        }
        const eventsToFetchNames = new Set<number>();
        const roomsToFetchNames = new Set<number>();
        const resourcesToFetchNames = new Set<number>();
        conflicts.spConflicts = conflicts.spConflicts || [];
        // sort conflicts by roomId and then by candidateRsrvStartDt
        conflicts.spConflicts.sort((a, b) => {
            const aRoomId = a.roomId ?? 0;
            const bRoomId = b.roomId ?? 0;
            if (aRoomId !== bRoomId) {
                return aRoomId - bRoomId;
            }
            const aStart = a.candidateRsrvStartDt ?? "";
            const bStart = b.candidateRsrvStartDt ?? "";
            return new Date(aStart).getTime() - new Date(bStart).getTime();
        });
        conflicts.spConflicts.forEach((spc) => {
            if (spc.roomId !== undefined) {
                roomsToFetchNames.add(spc.roomId);
            }
            spc.candidateEventId && eventsToFetchNames.add(spc.candidateEventId);
            if (spc.conflictEventId && spc.conflictType && ["rsrv", "pending"].includes(spc.conflictType)) {
                // note: blackouts use conflictEventId as the blackout id
                eventsToFetchNames.add(spc.conflictEventId);
            }
            spc.conflictTypeName = EventImportUtil.getConflictTypeName(spc.conflictType ?? "rsrv", true);
            spc.candidateDateTimeStr = ImportUtil.getDateTimeStr(
                spc.candidateRsrvStartDt ?? "",
                spc.candidateRsrvEndDt ?? "",
                this.timeFormat,
                this.dateTimeFormat,
            );
            spc.conflictDateTimeStr = ImportUtil.getDateTimeStr(
                spc.conflictRsrvStartDt ?? "",
                spc.conflictRsrvEndDt ?? "",
                this.timeFormat,
                this.dateTimeFormat,
            );
        });
        conflicts.rsConflicts = conflicts.rsConflicts || [];
        // sort conflicts by resourceId and then by candidateRsrvStartDt
        conflicts.rsConflicts.sort((a, b) => {
            const aResourceId = a.resourceId ?? 0;
            const bResourceId = b.resourceId ?? 0;
            if (aResourceId !== bResourceId) {
                return aResourceId - bResourceId;
            }
            const aStart = a.candidateRsrvStartDt ?? "";
            const bStart = b.candidateRsrvStartDt ?? "";
            return new Date(aStart).getTime() - new Date(bStart).getTime();
        });
        conflicts.rsConflicts.forEach((rsc) => {
            if (rsc.resourceId !== undefined) {
                resourcesToFetchNames.add(rsc.resourceId);
            }
            rsc.candidateEventId && eventsToFetchNames.add(rsc.candidateEventId);
            rsc.conflictEventId && eventsToFetchNames.add(rsc.conflictEventId);
            rsc.conflictTypeName = EventImportUtil.getConflictTypeName(rsc.conflictType ?? "rsrv", false);
            rsc.candidateDateTimeStr = ImportUtil.getDateTimeStr(
                rsc.candidateRsrvStartDt ?? "",
                rsc.candidateRsrvEndDt ?? "",
                this.timeFormat,
                this.dateTimeFormat,
            );
            rsc.conflictDateTimeStr = ImportUtil.getDateTimeStr(
                rsc.conflictRsrvStartDt ?? "",
                rsc.conflictRsrvEndDt ?? "",
                this.timeFormat,
                this.dateTimeFormat,
            );
        });

        const allNames = await NameService.getNames([
            ...Array.from(eventsToFetchNames).map((eventId) => {
                return { itemId: eventId, itemTypeId: Item.Ids.Event };
            }),
            ...Array.from(roomsToFetchNames).map((roomId) => {
                return { itemId: roomId, itemTypeId: Item.Ids.Location };
            }),
            ...Array.from(resourcesToFetchNames).map((resourceId) => {
                return { itemId: resourceId, itemTypeId: Item.Ids.Resource };
            }),
        ]);

        (conflicts.spConflicts || []).forEach((spc) => {
            if (spc.roomId !== undefined) {
                spc.roomName = allNames[NameService.itemKeyFromIds(spc.roomId, Item.Ids.Location)];
            }
            if (spc.candidateEventId !== undefined) {
                spc.candidateEventName = allNames[NameService.itemKeyFromIds(spc.candidateEventId, Item.Ids.Event)];
            }
            if (spc.conflictEventId !== undefined) {
                spc.conflictEventName = allNames[NameService.itemKeyFromIds(spc.conflictEventId, Item.Ids.Event)];
            }
        });

        (conflicts.rsConflicts || []).forEach((rsc) => {
            if (rsc.resourceId !== undefined) {
                rsc.resourceName = allNames[NameService.itemKeyFromIds(rsc.resourceId, Item.Ids.Resource)];
            }
            if (rsc.candidateEventId !== undefined) {
                rsc.candidateEventName = allNames[NameService.itemKeyFromIds(rsc.candidateEventId, Item.Ids.Event)];
            }
            if (rsc.conflictEventId !== undefined) {
                rsc.conflictEventName = allNames[NameService.itemKeyFromIds(rsc.conflictEventId, Item.Ids.Event)];
            }
        });

        this.type.conflicts = conflicts;
        this.refresh();
    }

    async importEvents(events: EventI[]): Promise<EventImportSaveResponseDto> {
        this.type.errors = [];
        if (!this.fallbackFolder?.itemId && !this.overrideFolder?.itemId) {
            this.type.errors.push("Please select a fallback or override folder");
            this.refresh();
            return {} as EventImportSaveResponseDto;
        }
        if (!events || !events.length) {
            this.type.errors.push("No valid events found");
            this.refresh();
            return {} as EventImportSaveResponseDto;
        }

        let fallbackCabinetId: number | null = null;
        let overrideCabinetId: number | null = null;
        if (this.overrideFolder?.itemId) {
            this.fallbackFolder = null;
        }
        if (this.fallbackFolder?.itemId) {
            this.overrideFolder = null;
            const fallbackFolder = await EventService.getFolderById(this.fallbackFolder?.itemId as number);
            if (fallbackFolder) {
                fallbackCabinetId = fallbackFolder.parent_id ?? null;
            }
        }
        if (this.overrideFolder?.itemId) {
            const overrideFolder = await EventService.getFolderById(this.overrideFolder.itemId as number);
            if (overrideFolder) {
                overrideCabinetId = overrideFolder.parent_id ?? null;
            }
        }

        const fallbackFolderId = S25Util.parseInt(this.fallbackFolder?.itemId);
        const overrideFolderIdValue = S25Util.parseInt(this.overrideFolder?.itemId);
        return ImportService.importEvents({
            events: events,
            fallbackFolderId: fallbackFolderId ?? 0,
            fallbackCabinetId: fallbackCabinetId ?? 0,
            overrideFolderId: overrideFolderIdValue ?? undefined,
            overrideCabinetId: overrideCabinetId ?? undefined,
            allowShared: true,
            allowBlackoutOverride: true,
            allowClosedHoursOverride: true,
            moveConflictsToPreferences: this.moveConflictsToPreferences,
        });
    }

    async ngOnInit() {
        this.type.onComplete = this.onCompleteEvents;

        this.timeFormat = await UserprefService.getS25Timeformat();
        this.dateTimeFormat = await UserprefService.getS25DateTimeformat();

        // vvv unfiltered name -> id maps vvv
        this.eventTypeNameMap = await EventTypeService.getSingleton()
            .get()
            .then((eventTypes) => {
                return eventTypes.reduce((map, et) => {
                    if (et && et.type_name && et.type_id !== undefined) {
                        map.set(et.type_name, et.type_id);
                    }
                    return map;
                }, new Map<string, number>());
            });

        this.orgNameMap = await OrganizationService.getAllOrganizationsList().then((orgs) => {
            return orgs.reduce((map, org) => {
                if (org && org.name && org.id !== undefined) {
                    map.set(org.name, org.id);
                }
                return map;
            }, new Map<string, number>());
        });

        this.catNameMap = await EventCategoryService.getSingleton()
            .get()
            .then((categories) => {
                return categories.reduce((map, cat) => {
                    map.set(cat.category_name, cat.category_id);
                    return map;
                }, new Map<string, number>());
            });

        this.custAttrNameMap = await CustomAttributeEventService.getSingleton()
            .get()
            .then((custAttrs) => {
                return custAttrs.reduce((map, ca) => {
                    map.set(ca.attribute_name, ca);
                    return map;
                }, new Map<string, CustomAttribute>());
            });
        // ^^^ unfiltered name -> id maps ^^^

        this.init = true;
        this.refresh();
    }

    refresh() {
        this.cd.detectChanges();
        this.parentCd?.detectChanges();
        ScrollLockService.unlockBodyScroll();
    }

    async downloadTemplate() {
        const templateData = [
            {
                EVENT_ID: "EVENT-001",
                EVENT_NAME: "Sample Event",
                EVENT_TITLE: "Sample Event Title",
                EVENT_TYPE: "Meeting",
                DESCRIPTION: "Event description",
                INTERNAL_NOTES: "Internal notes",
                PRIMARY_ORGANIZATION: "Sample Organization",
                SCHEDULER: "scheduler@example.com",
                REQUESTOR: "requestor@example.com",
                INSTRUCTOR: "instructor@example.com",
                CATEGORIES: "Category1|Category2",
                PROFILE_ID: "PROFILE-001",
                PROFILE_NAME: "Weekly Meeting",
                REG_HEADCOUNT: 25,
                EXP_HEADCOUNT: 30,
                FIRST_OCCURRENCE_DATE: "2024-01-15",
                START_TIME: "09:00:00",
                END_TIME: "10:00:00",
                PRE_EVENT_TIME: "15",
                POST_EVENT_TIME: "15",
                REPEATING_RULE: "W1 MO #3",
                ADHOC_DATES: "",
                LOCATIONS: "Room101:shared|Room102",
                RESOURCES: "Projector:2|Microphone:1",
                BOUND_PROFILE_ID: "",
                RELATED_EVENT_ID: "",
                CUSTOM_ATTRIBUTES: "Attribute1:Value1|Attribute2:Value2",
            },
        ];

        await SpreadSheetService.downloadExcel(templateData, "Event_Import_Template.xlsx", "Event Import Template");
    }
}
